import React, {memo, useState} from 'react';
import {Handle, Position} from 'reactflow';

import {Box, Stack, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {ArrowDown01Icon, ChatBotIcon, HeadsetIcon, PhoneCallIcon, PlayIcon} from "@hugeicons/core-free-icons";
import {useSetAtom} from "jotai";
import {toast} from "react-toastify";
import {ChatDrawerAtom, StartCallAtom} from "../pages/project/atoms";
import {TestChatOpenAtom} from "../layouts/registry-project-layout/atoms";
import {useRegistryProject} from "../pages/registry-project/RegistryProjectContext";
import {getPalette, hexToRgba, NODE_RADIUS, NODE_SHADOW} from "./nodeTheme";
import {BranchedMenuPopover} from "../components/branched-menu/BranchedMenu";

const START_MENU_ITEMS = [
  {key: "chatbot", label: "Chatbot", description: "Open the chat test drawer", icon: ChatBotIcon},
  {key: "web", label: "Web Call", description: "Talk to it over your mic", icon: HeadsetIcon},
  {key: "phone", label: "Phone Call", description: "Ring a real phone number", icon: PhoneCallIcon},
];

function StartNode({id, selected}) {
  const setChatDrawer = useSetAtom(ChatDrawerAtom);
  const setStartCall = useSetAtom(StartCallAtom);
  const palette = getPalette("start");
  const [anchorEl, setAnchorEl] = useState(null);
  // inside a registry project the node goes straight to its test chat; the old project pages keep the menu
  const {project, subProject} = useRegistryProject();
  const setTestChatOpen = useSetAtom(TestChatOpenAtom);
  const isRegistry = Boolean(project);

  const openMenu = (e) => {
    e.stopPropagation();
    if (isRegistry) {
      if (!subProject?.flowUid) {
        toast.error("This language has no flow to test");
        return;
      }
      setTestChatOpen(true);
      return;
    }
    setAnchorEl(e.currentTarget);
  };
  const closeMenu = () => setAnchorEl(null);

  const onSelect = (key) => {
    closeMenu();
    if (key === "chatbot") {
      setChatDrawer({open: true, startNode: id});
    } else {
      setStartCall({open: true, mode: key});
    }
  };

  return (
    <Box className={"node start-node"}
         sx={{
           display: "inline-flex",
           alignItems: "center",
           gap: 0.5,
           bgcolor: palette.accent,
           pl: 0.5, pr: 1, py: 0.5,
           borderRadius: `${NODE_RADIUS}px`,
           border: "1px solid",
           borderColor: palette.border,
           boxShadow: selected ? `${NODE_SHADOW}, 0 0 0 3px ${hexToRgba(palette.accent, 0.3)}` : NODE_SHADOW,
           transition: "box-shadow .18s ease, transform .18s ease",
         }}
    >
      <Stack
        direction={"row"} gap={0.75} alignItems={"center"}
        onClick={openMenu}
        aria-haspopup={"menu"}
        aria-expanded={Boolean(anchorEl)}
        sx={{
          color: "#FFF",
          cursor: "pointer",
          borderRadius: "999px",
          pl: 0.25, pr: 1, py: 0.25,
          background: hexToRgba("#FFFFFF", anchorEl ? 0.22 : 0.14),
          transition: "background-color .16s ease, transform .12s ease",
          "&:hover": {background: hexToRgba("#FFFFFF", 0.26), transform: "translateY(-0.5px)"},
          "&:active": {transform: "translateY(0)"},
        }}
      >
        <Box sx={{
          width: 22, height: 22, borderRadius: "50%", display: "grid", placeItems: "center",
          background: `linear-gradient(145deg, ${hexToRgba("#FFFFFF", 0.32)}, ${hexToRgba("#FFFFFF", 0.12)})`,
          boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.35)",
        }}>
                    <HugeiconsIcon icon={PlayIcon} size={11} strokeWidth={2}/>
        </Box>
        <Typography sx={{fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase"}}>
          Start
        </Typography>
        {!isRegistry && (
          <HugeiconsIcon icon={ArrowDown01Icon} size={13} strokeWidth={2.2} style={{opacity: 0.85, transform: anchorEl ? "rotate(180deg)" : "none", transition: "transform .16s ease"}}/>
        )}
      </Stack>

      <BranchedMenuPopover
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        items={START_MENU_ITEMS}
        onSelect={onSelect}
        accent={palette.accent}
        labelId={`start-node-menu-${id}`}
        // Light blur: the pill sits inside the transformed react-flow canvas and can't be
        // lifted above a full-screen backdrop, so keep it gentle (2px) — softens the canvas
        // while the pill's own blur stays subtle.
        blur={2}
        anchorOrigin={{vertical: "bottom", horizontal: "left"}}
        transformOrigin={{vertical: "top", horizontal: "left"}}
      />

      <Handle type="source" position={Position.Right} id={id}/>
    </Box>
  );
}

export default memo(StartNode);
